import * as THREE from 'three'
import { Vec3, sub } from '../core/math/vec3'
import { Shape } from '../core/vessel/part'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { SimVessel, Simulation } from '../game/simulation'
import { toScene } from './renderer'

const COLORI: Record<string, number> = {
  command: 0xe4e7ea,
  tank: 0xb9c0c7,
  engine: 0x5b6068,
  decoupler: 0xd08b3a,
  aero: 0x3c424a,
  structural: 0x9aa2aa,
  parachute: 0xcfc7a8,
  heatshield: 0x5a4030,
  leg: 0x7b838b,
  rcs: 0x8b939b,
}

function geometry(shape: Shape): THREE.BufferGeometry {
  switch (shape.kind) {
    // in Three.js l'asse di cilindri e coni e gia +Y: come il muso del razzo
    case 'cylinder': return new THREE.CylinderGeometry(shape.radius, shape.radius, shape.height, 24)
    case 'cone': return new THREE.ConeGeometry(shape.radius, shape.height, 24)
    case 'box': return new THREE.BoxGeometry(shape.x, shape.y, shape.z)
  }
}

function plumeMesh(radius: number): THREE.Mesh {
  const g = new THREE.ConeGeometry(radius * 0.85, 1, 16, 1, true)
  g.translate(0, -0.5, 0)      // la punta resta all'ugello, il cono scende
  g.rotateX(Math.PI)           // e si apre verso il basso
  return new THREE.Mesh(g, new THREE.MeshBasicMaterial({
    color: 0xffc27a,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }))
}

interface Vista {
  readonly group: THREE.Group
  readonly firma: string
  readonly plumes: Map<string, THREE.Mesh>
  meshCount: number
}

function costruisci(sv: SimVessel, catalog: PartCatalog): Vista {
  const group = new THREE.Group()
  const plumes = new Map<string, THREE.Mesh>()
  let meshCount = 0

  for (const p of sv.vessel.parts) {
    const def = partDef(catalog, p.defId)
    const mesh = new THREE.Mesh(geometry(def.shape), new THREE.MeshStandardMaterial({
      color: COLORI[def.category] ?? 0x9aa2aa,
      roughness: def.category === 'engine' ? 0.42 : 0.72,
      metalness: def.category === 'engine' ? 0.75 : 0.18,
      flatShading: true,
    }))
    // le coordinate delle parti sono relative all'origine locale, ma lo stato
    // del corpo rigido segue il baricentro: da qui la sottrazione
    const rel = sub(p.position, sv.massProps.com)
    mesh.position.set(rel.x, rel.y, rel.z)
    mesh.quaternion.set(p.rotation.x, p.rotation.y, p.rotation.z, p.rotation.w)
    group.add(mesh)
    meshCount++

    if (def.engine) {
      const raggio = def.shape.kind === 'box' ? 0.5 : def.shape.radius
      const fiamma = plumeMesh(raggio)
      fiamma.position.set(rel.x, rel.y - (def.shape.kind === 'box' ? 0 : def.shape.height / 2), rel.z)
      fiamma.visible = false
      group.add(fiamma)
      plumes.set(p.id, fiamma)
    }
  }

  return { group, firma: sv.vessel.parts.map((p) => p.id).sort().join('|'), plumes, meshCount }
}

function smonta(v: Vista): void {
  v.group.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.geometry) m.geometry.dispose()
    const mat = m.material as THREE.Material | THREE.Material[] | undefined
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
    else mat?.dispose()
  })
  v.group.removeFromParent()
}

export interface VesselViews {
  /** allinea la scena ai veicoli della simulazione */
  sync(sim: Simulation, origin: Vec3, throttleDi: (sv: SimVessel) => number): void
  meshCount(): number
  plumeCount(): number
  groupCount(): number
}

export function createVesselViews(scene: THREE.Scene, catalog: PartCatalog): VesselViews {
  const viste = new Map<string, Vista>()

  return {
    sync(sim, origin, throttleDi) {
      const vivi = new Set(sim.vessels.map((v) => v.id))
      for (const [id, v] of viste) {
        if (!vivi.has(id)) { smonta(v); viste.delete(id) }
      }

      for (const sv of sim.vessels) {
        const firma = sv.vessel.parts.map((p) => p.id).sort().join('|')
        let vista = viste.get(sv.id)
        // dopo una separazione l'insieme delle parti cambia: si ricostruisce
        if (!vista || vista.firma !== firma) {
          if (vista) smonta(vista)
          vista = costruisci(sv, catalog)
          scene.add(vista.group)
          viste.set(sv.id, vista)
        }

        const p = toScene(sv.state.position, origin)
        vista.group.position.copy(p)
        vista.group.quaternion.set(
          sv.state.orientation.x, sv.state.orientation.y,
          sv.state.orientation.z, sv.state.orientation.w,
        )

        const manetta = throttleDi(sv)
        for (const [engId, fiamma] of vista.plumes) {
          const acceso = !sv.debris && sv.activeEngines.includes(engId) && manetta > 0.01
          fiamma.visible = acceso
          if (acceso) {
            const lunghezza = 3 + 9 * manetta
            // un po' di tremolio: una fiamma perfettamente ferma sembra plastica
            const tremolio = 0.9 + 0.2 * Math.sin(sim.time * 37 + engId.length)
            fiamma.scale.set(1, lunghezza * tremolio, 1)
          }
        }
      }
    },
    meshCount: () => [...viste.values()].reduce((s, v) => s + v.meshCount, 0),
    plumeCount: () => [...viste.values()]
      .reduce((s, v) => s + [...v.plumes.values()].filter((m) => m.visible).length, 0),
    groupCount: () => viste.size,
  }
}
